/**
 * Feature Flags model.
 *
 * Single Firestore document at `config/featureFlags` that controls
 * site-wide UI/behavioural toggles. One document keeps reads cheap
 * (every page that gates on a flag does a single doc subscribe) and
 * dodges the composite-index dance a per-flag collection would
 * require.
 *
 * Add new flags by extending {@link FeatureFlags} and
 * {@link DEFAULT_FEATURE_FLAGS}. The admin page picks them up
 * automatically as long as they appear in the type — readers should
 * always fall back to defaults so a missing field never bricks the
 * page that consumes it.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

import type { Timestamp } from 'firebase/firestore';

/**
 * Site-wide feature flag values. New flags MUST get a sensible
 * default in {@link DEFAULT_FEATURE_FLAGS} so consumers can render
 * before the document loads.
 */
export interface FeatureFlags {
  /**
   * Master switch for the in-app Officer Messages feature. When
   * true the sidebar entry, dashboard card, /admin/messages route
   * and contact-form Firestore writes all stay live. When false the
   * feature is fully disabled — UI hidden, route returns access
   * denied, and the public contact form skips the Firestore write
   * (so submissions only reach configured email recipients, if
   * any). Backward compat: an older deploy may have written this
   * value under the legacy `adminMessagesEnabled` field — readers
   * pick up either.
   */
  officerMessagesEnabled: boolean;

  /**
   * Route public-facing submissions (contact form, membership
   * application) through email notifications in addition to (or
   * instead of) the in-app inbox. When false, no extra emails are
   * sent beyond the existing officer notifications.
   */
  emailContactEnabled: boolean;

  /**
   * Recipients for general contact-form submissions. Only used when
   * `emailContactEnabled` is true. Empty array is valid — feature
   * stays "on" but nothing gets emailed until the operator adds
   * addresses.
   */
  contactFormEmailRecipients: string[];

  /**
   * Recipients for completed membership-registration emails. Same
   * rules as `contactFormEmailRecipients`.
   */
  registrationEmailRecipients: string[];
}

/**
 * Persisted shape — the live Firestore doc adds audit metadata on
 * top of the user-controlled flags.
 */
export interface FeatureFlagsDoc extends FeatureFlags {
  /** Server timestamp of the last admin write. */
  updatedAt?: Timestamp;
  /** UID of the admin who last wrote the doc. */
  updatedBy?: string;
}

/**
 * Conservative defaults — assume the existing in-app messaging
 * surface stays available (legacy behaviour) and email blasting is
 * opt-in. Returned when the doc doesn't exist yet.
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  officerMessagesEnabled: true,
  emailContactEnabled: false,
  contactFormEmailRecipients: [],
  registrationEmailRecipients: [],
};

/**
 * Raw legacy doc shape — older deploys wrote
 * `adminMessagesEnabled` before the rename. Reader helpers should
 * accept either field and prefer the new one when both are present.
 */
export interface LegacyFeatureFlagsDoc {
  /** @deprecated renamed to officerMessagesEnabled */
  adminMessagesEnabled?: boolean;
}

/** Firestore path constants — single source of truth for callers. */
export const FEATURE_FLAGS_COLLECTION = 'config';
export const FEATURE_FLAGS_DOC = 'featureFlags';
